import React from "react";
import PropTypes from "prop-types";
import { ButtonRow, CFSTheading, CustomDropdownSelect, CustomErrBlock, CustomImg, FlexColumn, TextLabel } from "../MetricDashboard.styles";
import { barChartOptions, pieChartOptions } from "./constants";
import { Wrapper } from "../../../constants/common/StyledGlobalConstants";
import CustomChart from "./CustomChart";
import Colors from "../VCCPButtons/Colors";
import { USA_And_CAN_States } from "../../../util/addressUtils";
import { cfstComparisonViewDropDownFilterValues } from "../../../constants/DropdownConstants";
import WarningIcon from "../../../assets/icons/NewWarningIcon.png";

const ComparisionView = ({
  notificationComparison,
  dashboardComparisionData,
  setCfstComparisonViewFilterValue,
  jobTrendsNotification,
  onlineVsOfflineJobsTrendNotification,
}) => {
  const CFST_ComparisionData = (localData = [], IsStatesIncluded = false) => {
    return {
      labels: IsStatesIncluded
        ? localData?.map((item) => USA_And_CAN_States[item.value.toString()] || item.value)
        : localData?.map((item) => (item.value === "NA" ? "No Accessories" : item.value)),
      datasets: [
        {
          backgroundColor: Colors.slice(0, localData?.length),
          borderColor: Colors.slice(0, localData?.length),
          data: localData?.map((item) => item.count),
        },
      ],
    };
  };

  const CFST_ComboChartData = (localData = []) => {
    const labels = Object.keys(localData);
    const dataArr = labels.map((label) => localData[label]);
    return {
      labels: labels,
      datasets: [
        {
          label: "Scheduled Jobs",
          backgroundColor: Colors[0],
          borderColor: Colors[0],
          data: dataArr.map((item) => item.scheduled),
        },
        {
          label: "Rescheduled Jobs",
          backgroundColor: Colors[1],
          borderColor: Colors[1],
          data: dataArr?.map((item) => item.rescheduled),
        },
        {
          label: "Completed Jobs",
          backgroundColor: Colors[2],
          borderColor: Colors[2],
          data: dataArr?.map((item) => item.completed),
        },
        {
          label: "Partially Completed Jobs",
          backgroundColor: Colors[3],
          borderColor: Colors[3],
          data: dataArr?.map((item) => item.partiallyCompleted),
        },
      ],
    };
  };

  const CFST_OnlineVsOfflineData = (monthWiseTrends) => {
    const cfstData = monthWiseTrends?.CFST || {};
    const c360Data = monthWiseTrends?.C360 || {};

    const allMonths = Array.from(new Set([...Object.keys(cfstData), ...Object.keys(c360Data)]));

    const sumAttributes = (data) =>
      ["scheduled", "rescheduled", "completed", "partiallyCompleted", "open", "closed", "cancelled"].reduce(
        (sum, key) => sum + Number(data?.[key] ?? 0),
        0
      );
    return {
      labels: allMonths,
      datasets: [
        {
          label: "Online Jobs",
          backgroundColor: Colors[4],
          borderColor: Colors[4],
          data: allMonths.map((month) => sumAttributes(cfstData[month])),
        },
        {
          label: "Offline Jobs",
          backgroundColor: Colors[5],
          borderColor: Colors[5],
          data: allMonths.map((month) => sumAttributes(c360Data[month])),
        },
      ],
    };
  };

  const handleDropdownChange = (e) => {
    setCfstComparisonViewFilterValue(e.target.value);
  };

  return (
    <>
      <CFSTheading>Comparison view</CFSTheading>
      {notificationComparison && (
        <CustomErrBlock>
          <CustomImg src={WarningIcon} alt="warning" />
          Unable to fetch data due to an unexpected error
        </CustomErrBlock>
      )}
      <FlexColumn style={{ margin: "34px 20px" }}>
        <TextLabel style={{ width: "30%" }}>Filter For</TextLabel>
        <CustomDropdownSelect onChange={(e) => handleDropdownChange(e)} defaultChecked={"1"}>
          {cfstComparisonViewDropDownFilterValues.map((item) => {
            return (
              <option key={item.id} value={item.value}>
                {item.label}
              </option>
            );
          })}
        </CustomDropdownSelect>
      </FlexColumn>
      {!notificationComparison && (
        <>
          <Wrapper style={{ width: "100%", marginTop: "25px" }}>
            <CustomChart
              chartType="barWithFullWidth"
              chartLabel="Jobs Trend"
              chartData={CFST_ComboChartData(dashboardComparisionData?.monthWiseTrends?.CFST)}
              options={{
                ...barChartOptions,
                plugins: {
                  ...barChartOptions.plugins,
                  legend: {
                    ...(barChartOptions.plugins?.legend || {}),
                    display: true,
                  },
                },
              }}
              jobTrendsNotification={jobTrendsNotification}
            />
          </Wrapper>
          <ButtonRow>
            <Wrapper flex="1">
              <CustomChart
                chartType="bar"
                chartLabel="Top 5 States"
                chartData={CFST_ComparisionData(dashboardComparisionData?.top5States, true)}
                options={barChartOptions}
              />
            </Wrapper>
            <Wrapper flex="1">
              <CustomChart
                chartType="bar"
                chartLabel="Top 5 Vendors"
                chartData={CFST_ComparisionData(dashboardComparisionData?.top5Vendors)}
                options={barChartOptions}
              />
            </Wrapper>
          </ButtonRow>

          <ButtonRow>
            <Wrapper flex="1">
              <CustomChart
                chartType="bar"
                chartLabel="Top 5 Features"
                chartData={CFST_ComparisionData(dashboardComparisionData?.top5Features)}
                options={barChartOptions}
              />
            </Wrapper>

            <Wrapper flex="1">
              <CustomChart
                chartType="pie"
                chartLabel="Reschedule Reasons"
                chartData={CFST_ComparisionData(dashboardComparisionData?.rescheduleReasons)}
                options={pieChartOptions}
              />
            </Wrapper>
          </ButtonRow>
          <Wrapper style={{ width: "100%", marginTop: "25px" }}>
            <CustomChart
              chartType="barWithFullWidth"
              chartLabel="Online Jobs Vs Offline Jobs Trend"
              chartData={CFST_OnlineVsOfflineData(dashboardComparisionData?.monthWiseTrends)}
              options={{
                ...barChartOptions,
                plugins: {
                  ...barChartOptions.plugins,
                  legend: {
                    ...(barChartOptions.plugins?.legend || {}),
                    display: true,
                  },
                },
              }}
              onlineVsOfflineJobsTrendNotification={onlineVsOfflineJobsTrendNotification}
            />
          </Wrapper>
        </>
      )}
    </>
  );
};

ComparisionView.propTypes = {
  notificationComparison: PropTypes.node,
  dashboardComparisionData: PropTypes.shape({
    top5States: PropTypes.array,
    top5Vendors: PropTypes.array,
    top5Features: PropTypes.array,
    rescheduleReasons: PropTypes.array,
    monthWiseTrends: PropTypes.object,
  }),
  setCfstComparisonViewFilterValue: PropTypes.func.isRequired,
  jobTrendsNotification: PropTypes.node,
  onlineVsOfflineJobsTrendNotification: PropTypes.node,
};

export default ComparisionView;